export function utmLink(values, { normalize = true, dashes = true } = {}) {
  const url = new URL(values.url);
  if (
    !["http:", "https:"].includes(url.protocol) ||
    url.username ||
    url.password
  )
    throw new Error(
      "Enter an HTTP or HTTPS destination URL without credentials.",
    );
  for (const key of ["source", "medium", "campaign", "term", "content"]) {
    let value = (values[key] || "").trim();
    if (normalize) value = value.toLowerCase();
    if (dashes) value = value.replace(/\s+/g, "-");
    if (value) url.searchParams.set("utm_" + key, value);
    else url.searchParams.delete("utm_" + key);
  }
  return url.href;
}
export function publicSchema(type, values) {
  if (!["Organization", "LocalBusiness", "WebSite", "Article", "BlogPosting", "Product", "Service", "Event", "Person", "BreadcrumbList", "VideoObject", "Course", "SoftwareApplication"].includes(type))
    throw new Error("Choose a supported schema type.");
  if (!values.name?.trim() || !values.url?.trim())
    throw new Error("Name and URL are required.");
  const url = new URL(values.url);
  if (!["http:", "https:"].includes(url.protocol) || url.username || url.password)
    throw new Error("Use an HTTP or HTTPS URL.");
  if (type === "BreadcrumbList") {
    const list = [];
    if (values.parentName && values.parentUrl) {
      const parent = new URL(values.parentUrl);
      if (!["http:", "https:"].includes(parent.protocol))
        throw new Error("Use a public parent URL.");
      list.push({
        "@type": "ListItem",
        position: 1,
        name: values.parentName,
        item: parent.href,
      });
    }
    list.push({
      "@type": "ListItem",
      position: list.length + 1,
      name: values.name,
      item: url.href,
    });
    return {
      "@context": "https://schema.org",
      "@type": type,
      itemListElement: list,
    };
  }
  const data = {
    "@context": "https://schema.org",
    "@type": type,
    name: values.name,
    url: url.href,
  };
  if (values.description) data.description = values.description;
  if (["Article", "BlogPosting"].includes(type)) {
    data.headline = values.name;
    delete data.name;
    if (values.author) data.author = { "@type": "Person", name: values.author };
    if (values.datePublished) data.datePublished = values.datePublished;
  }
  if (type === "Product" && values.price && values.currency) {
    if (!Number.isFinite(Number(values.price)) || Number(values.price) < 0 || !/^[A-Z]{3}$/.test(values.currency))
      throw new Error("Supply a non-negative price and a three-letter uppercase currency code.");
    data.offers = {
      "@type": "Offer",
      price: values.price,
      priceCurrency: values.currency,
    };
  }
  if (type === "Event") {
    if (values.startDate) data.startDate = values.startDate;
    if (values.location)
      data.location = { "@type": "Place", name: values.location };
  }
  return data;
}
export function invoiceTotals(items, tax, discount) {
  const rate = Number(tax),
    reduction = Number(discount);
  if (
    !Number.isFinite(rate) ||
    rate < 0 ||
    !Number.isFinite(reduction) ||
    reduction < 0
  )
    throw new Error("Tax and discount must be non-negative numbers.");
  let subtotal = 0;
  for (const item of items) {
    const quantity = Number(item.quantity),
      price = Number(item.price);
    if (
      item.quantity === "" ||
      item.price === "" ||
      !Number.isFinite(quantity) ||
      !Number.isFinite(price) ||
      quantity < 0 ||
      price < 0
    )
      throw new Error("Enter valid quantities and unit prices.");
    subtotal += quantity * price;
  }
  if (reduction > subtotal)
    throw new Error("Discount cannot exceed the subtotal.");
  const taxable = subtotal - reduction,
    round = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
  if (!Number.isFinite(taxable * (1 + rate / 100)) || subtotal > 1e12 || taxable * (1 + rate / 100) > 1e12)
    throw new Error("Invoice amounts are too large. Use amounts below one trillion.");
  return {
    subtotal: round(subtotal),
    discount: round(reduction),
    tax: round((taxable * rate) / 100),
    total: round(taxable * (1 + rate / 100)),
  };
}
export function savingsResult(values) {
  const n = values.map(Number);
  if (
    values.some((v) => v === "") ||
    n.some((v) => !Number.isFinite(v) || v < 0) ||
    n[3] > 100
  )
    throw new Error(
      "Fill every field with non-negative numbers; automation must be 0-100%.",
    );
  const [employees, hourly, hours, percent, cost] = n,
    monthlyHours = (((employees * hours * 52) / 12) * percent) / 100,
    benefit = monthlyHours * hourly,
    round = (n) => Number(n.toFixed(2));
  if (![monthlyHours, benefit, (benefit - cost) * 12, cost ? (benefit - cost) / cost * 100 : 0].every(Number.isFinite))
    throw new Error("These assumptions produce values too large to calculate.");
  return {
    hoursSavedPerMonth: round(monthlyHours),
    laborValuePerMonth: round(benefit),
    monthlyToolCost: cost,
    annualNetValue: round((benefit - cost) * 12),
    roiPercent: cost ? round(((benefit - cost) / cost) * 100) : null,
  };
}
